import {
  isPlanV2CurrentPeriodRecord,
  type PlanV2IssueDispositionRecord,
  type PlanV2IssueType
} from './plan-v2-issues';

/**
 * Which pages the operator has already dealt with this period.
 *
 * The belt exists to hand back one decision at a time and then take it away
 * again. It only ever managed half of that: `ignoredUrlIds()` retired the pages
 * someone explicitly Put aside, and nothing retired the pages they had actually
 * worked. A page whose every card had been sent to work items stayed on the
 * belt as an empty screen with no Send button on it — and because which cards a
 * page carries differs per page, the ones that stalled looked arbitrary rather
 * than finished.
 *
 * So: a page the operator has decided leaves the belt, and this module is the
 * one place that says what "decided" means. It is a pure rule over disposition
 * records, kept out of the queue service so it can be read and tested without
 * an HTTP client — and mirrored server-side by `loadDecidedUrlIds` in
 * `backend/src/services/impact-brief-load.ts`, which feeds the dashboard's
 * progress counts from the same rule. The two must agree: the strip saying
 * "12 of 18 done" over a belt of nine is the bug this replaces, wearing a
 * different hat.
 */

/**
 * Dispositions keyed by CHECK CODE rather than by page.
 *
 * These two are the whole subtlety. `check_muted` means "show this check
 * unticked on the next page I open"; `site_wide_technical` means "this is a
 * template defect, stop asking me about it on every page". Both are written
 * from whichever page the operator happened to be standing on when they decided
 * it, and neither says anything about that page.
 *
 * Counting one as a decision would retire a page from the belt on the strength
 * of a preference about every page — and specifically retire whichever page was
 * open at the time, which from the operator's side is indistinguishable from
 * the queue dropping a page at random. That is the bug this module exists to
 * fix, so reintroducing it here would be a poor trade.
 *
 * Expressed as the exceptions rather than as an allow-list of page-scoped
 * types: a new issue type is page-scoped unless someone says otherwise, and the
 * failure mode of forgetting to list it is a page that stays in the belt one
 * visit too long, not a page that vanishes from it.
 */
export const CHECK_SCOPED_ISSUE_TYPES: readonly PlanV2IssueType[] = [
  'check_muted',
  'site_wide_technical'
];

/** True when a disposition of this type is a verdict on the page it names. */
export function isPageScopedIssueType(issueType: PlanV2IssueType | null | undefined): boolean {
  if (!issueType) return false;
  return !CHECK_SCOPED_ISSUE_TYPES.includes(issueType);
}

/**
 * True when this record retires its page from the belt.
 *
 * Both queues count. Sending a card to work items and dismissing it are
 * opposite verdicts on the same question, and the belt asks the question, not
 * the verdict — an operator who dismissed every card on a page has dealt with
 * that page exactly as thoroughly as one who claimed them all.
 *
 * `url_ignored` is in, and is the reason this set subsumes `ignoredUrlIds()`
 * rather than sitting beside it: Put aside is a decision.
 *
 * Previous-period rows are out. They are history the operator compares against
 * (LIVING_BRIEF_PLAN §1), not work done this week; counting them would empty a
 * fresh period's belt on the morning it opened. Mirrors the server's
 * `archivedAt: null`.
 */
export function isDecidingDisposition(
  record:
    | Pick<PlanV2IssueDispositionRecord, 'issueType' | 'urlId' | 'periodBucket' | 'queue'>
    | null
    | undefined
): boolean {
  if (!record) return false;
  if (!(record.urlId || '').trim()) return false;
  if (!isPlanV2CurrentPeriodRecord(record)) return false;
  if (!isPageScopedIssueType(record.issueType)) return false;

  // A DISMISSAL decides the card it names, not the page it sits on — the same
  // rule the server applies in `dispositionDecidesPage`, and this is the copy
  // that actually empties the belt.
  //
  // The server was fixed first and this was missed, so the Dashboard offered
  // 44 pages while the belt built 43: /templates/gantt-chart-template-for-
  // google-sheets was back in the brief and still skipped on entry, because
  // two dismissed Technical cards read here as "page done". Two derivations of
  // one fact, and the operator sees whichever one is wrong.
  //
  // Finishing a page takes a page-level decision: Put aside (`url_ignored`) or
  // work actually queued off it.
  return record.issueType === 'url_ignored' || record.queue === 'work_item';
}

/**
 * The urlIds that have left the belt, from this period's disposition records.
 *
 * One page routinely carries several dispositions — a technical card and a
 * section gap are two rows about one URL — and the set collapses them, because
 * the question the belt asks is "does this page still owe me a decision", not
 * "how many decisions did it take".
 */
export function decidedUrlIdsFrom(
  records: Iterable<PlanV2IssueDispositionRecord> | null | undefined
): Set<string> {
  const ids = new Set<string>();
  for (const record of records ?? []) {
    if (!isDecidingDisposition(record)) continue;
    ids.add(record.urlId.trim());
  }
  return ids;
}
